import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Heading,
  Button,
  TextField,
  ButtonGroup
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  NavHeader,
  Loader,
  getTranslatedText,
  TableContainer,
  ContentDeciderContainer,
  SkeletonTableLoading,
  EventEmitter,
  Tag,
  CustomModal,
  AccessControl,
  useCheckFeatureGating
} from '@nile/components';
import { NavLink } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import envConfig from '@nile/home/src/Config/env';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import InvoiceBuilderContext, {
  InvoiceBuilderContextProvider
} from '@nile/client_management/src/Contexts/InvoiceBuilderContext';
import {
  GET_SERVICE_CATEGORIES,
  AccountClient,
  useQuery,
  GET_WHITELISTED_SERVICE_ITEMS,
  CONSTRUCT_INVOICE_ITEMS
} from '@nile/graphql';
import { cloneDeep, debounce } from 'lodash';
import nanoId from 'nanoid';
import { Tooltip } from 'react-clone-tippy';
import NewServiceCatalog from '@nile/banyan/src/Pages/ServiceCatalog/NewServiceCatalog';
import CurrencyCell from './CurrencyCell';
import ItemsToInvoiceTable from './ItemsToInvoiceTable';
import { CreateServiceCatalogItem } from '@nile/banyan/src/Pages/ServiceCatalogNew/CreateServiceCatalogItem.tsx';
import { useFlags } from 'launchdarkly-react-client-sdk';

const NavHead = styled.div`
  font-size: 12px;
  font-weight: 700;
  padding: 8px 2px;
  padding-top: 0;
  border-bottom: 1px solid #dfdfdf;
  margin: 0 8px;
  margin-bottom: 8px;
  margin-top: 10px;
  a {
    display: block;
  }
`;

const SelectionItem = styled.div`
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
  background: ${(props) =>
    props.selected ? '#F5F5FC !important' : 'transparent'};
  font-weight: ${(props) => (props.selected ? '600' : 'normal')};
`;

function ItemsToInvoice(props) {
  const {
    intl,
    closeModal,
    invoiceData,
    noAudit,
    generateSiteSpecific
  } = props;
  const { loading, data, error } = useQuery(GET_SERVICE_CATEGORIES, {
    client: AccountClient,
    fetchPolicy: 'network-only'
  });
  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const [selectedSection, setSelectedSection] = useState(
    noAudit ? 'all' : 'audit'
  );
  const [allServiceItems, setAllServiceItems] = useState(null);
  const [filterText, setFilterText] = useState(null);
  const [records, setRecords] = useState({});
  const [listInfo, setListInfo] = useState({});
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  const fetchInvoiceTableDataRef = useRef(null);
  const [showNewServiceItemSheet, setShowNewServiceItemSheet] = useState();

  const isNewContracts = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const setSelectedRecords = (recordsInput, overrideSelectedSection) => {
    const allRecordsSet = [...new Set(recordsInput)];
    const selectedSectionItem =
      overrideSelectedSection || selectedSection || 'all';
    setRecords({
      ...records,
      [selectedSectionItem]: allRecordsSet
    });
  };

  const flags = useFlags();

  const setRecordsForMultipleSections = (properties) => {
    setRecords({
      ...records,
      ...properties
    });
  };

  const [
    getConstructedItems,
    {
      loading: constructedItemsLoading,
      data: constructedItems,
      error: constructedItemsError
    }
  ] = useLazyQuery(CONSTRUCT_INVOICE_ITEMS, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      if (res?.constructInvoiceItems) {
        const newRecords = res?.constructInvoiceItems.map((el) => {
          return {
            itemId: `audit-item-${nanoId()}`,
            ...el
          };
        });
        props.updateProperties({
          properties: {
            items: invoiceData.items
              ? invoiceData.items.concat(newRecords)
              : newRecords
          }
        });
      }

      setRecords({});
      closeModal();
    }
  });
  useEffect(() => {
    // getContainerData(selectedSection);
  }, [selectedSection]);
  const fetchFilteredData = (text) => {
    EventEmitter.dispatch('filterData', text);
  };

  const debouncedFilter = useCallback(debounce(fetchFilteredData, 1000), []);
  if (loading) return <Loader />;
  const allCategories = data.getServiceCategories
    ? data.getServiceCategories.slice()
    : [];
  // allCategories.unshift({
  //   name: 'all',
  //   label: getTranslatedText(intl, 'invoiceBuilder.itemSelection.allCategories')
  // });
  const handleSelection = () => {
    let auditRecords = [];
    let otherRecords = [];
    Object.keys(records).forEach((el) => {
      if (el === 'audit') auditRecords = records[el];
      else {
        otherRecords = otherRecords.concat(records[el]);
      }
    });

    if (otherRecords.length) {
      let newRecords = otherRecords.map((el) => {
        return {
          itemId: `catalog-item-${nanoId()}`,
          serviceItem: el,
          serviceCatalogItem: el,
          discountRate: null,
          billedDate: null,
          details: el[contractsNew ? 'description' : 'invoiceDescription'],
          quantity: null,
          unitPrice: contractsNew
            ? el?.sellingPrice?.details[0].value
            : el.quantityType === 'HOURS'
            ? el.businessHoursUnitPrice || null
            : el.unitPrice,
          amount: el.amount || '0',
          taxable: contractsNew ? el.taxable : el.salesTaxEnabled,
          tax: el.salesTax
        };
      });
      newRecords = newRecords.map((el) => {
        if (props.taxExempted) {
          return {
            ...el,
            taxable: false,
            tax: null
          };
        } else {
          return el;
        }
      });
      props.updateProperties({
        properties: {
          items: invoiceData.items
            ? invoiceData.items.concat(newRecords)
            : newRecords
        }
      });
      if (!auditRecords.length) {
        setRecords({});
        closeModal();
      }
    }
    if (auditRecords.length) {
      getConstructedItems({
        variables: {
          constructInvoiceItems: auditRecords.map((el) => {
            return {
              itemId: el.itemId
            };
          })
        },
        input: {
          constructInvoiceItems: auditRecords.map((el) => {
            return {
              itemId: el.itemId
            };
          })
        }
      });
    }
  };

  const contextValue = {
    records: records,
    setSelectedRecords: setSelectedRecords,
    setRecordsForMultipleSections: setRecordsForMultipleSections,
    selectedSection: selectedSection
  };
  let allSelectedItems = [];
  Object.keys(records).forEach((el) => {
    allSelectedItems = allSelectedItems.concat(records[el] || []);
  });
  const itemsSentToInvoiceFromBillingQueue =
    invoiceData?.items?.filter((el) => {
      return el?.itemId?.includes('audit-item');
    }) || [];
  const checkRowHighlight = (item) => {
    return (
      allSelectedItems?.findIndex(
        (el) => el.itemId === item?.original?.itemId
      ) > -1 ||
      itemsSentToInvoiceFromBillingQueue?.findIndex(
        (el) => el.offeredItem?.itemId === item?.original?.itemId
      ) > -1
    );
  };
  const checkDisabledRow = (item) => {
    return (
      itemsSentToInvoiceFromBillingQueue?.findIndex(
        (el) => el.offeredItem?.itemId === item?.original?.itemId
      ) > -1
    );
  };

  return (
    <div className="w1200 vh90 bg-white rounded-2xl ">
      <InvoiceBuilderContextProvider value={contextValue}>
        <div className="flex center p2040">
          <Heading>
            <FormattedMessage id="invoiceBuilder.itemSelection.modal.header" />
          </Heading>
          <div className="w200 ml-20">
            <TextField
              value={filterText}
              placeholder={getTranslatedText(
                intl,
                'invoiceBuilder.catalogItems.search.placeholder'
              )}
              onChange={(val) => {
                setFilterText(val);
                // setting this to null as it's just a gimmick to focus all categories. Else API call will go for all instead of filtered text.
                setSelectedSection(null);
                debouncedFilter(val);
              }}
              autoComplete={false}
              prefix={<SVGIcon name="Search" />}
              suffix={
                filterText ? (
                  <span
                    className="position-relative z21"
                    onClick={() => {
                      setFilterText(null);
                      debouncedFilter('');
                    }}
                  >
                    <SVGIcon name="CircleCancelMinor" />
                  </span>
                ) : null
              }
              size="small"
              variant="outline"
            />
          </div>
          <div className="mlauto flex center">
            {allSelectedItems?.length > 0 && (
              <Tooltip
                distance={5}
                html={
                  <div className="p15 w300 max-height-500 scroll bshadow bg-white">
                    {allSelectedItems.map((el) => {
                      return (
                        <div className="flex center py8 fsize12 white-space-wrap">
                          <div className="self-start">
                            {el?.serviceItem?.quantityType === 'UNITS' ||
                            el.quantityType === 'UNITS' ? (
                              <SVGIcon name="Quantity" />
                            ) : (
                              <SVGIcon name="Worklog" />
                            )}
                          </div>
                          <div className="ml8 self-start">
                            {el.name || el.serviceItem?.name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                }
                position="bottom-end"
                inertia
                trigger="mouseenter"
                animation="shift"
                interactive
                animateFill={false}
                theme="super-light"
              >
                <Tag
                  className="ml8 mr8 pointer border-black"
                  background="#FFFEF0"
                >
                  {`${allSelectedItems.length} ${getTranslatedText(
                    intl,
                    'admin.serviceCatalog.list.selected'
                  )}`}
                </Tag>
              </Tooltip>
            )}
            <ButtonGroup>
              {!isNewContracts ? (
                <AccessControl
                  roleToCheck="serviceCatalog.add"
                  render={
                    <Button
                      icon={<SVGIcon name="Add" />}
                      onClick={() => {
                        setShowNewServiceItemSheet(true);
                      }}
                    >
                      <FormattedMessage id="client.contractTemplate.form.createItem" />
                    </Button>
                  }
                />
              ) : (
                <CreateServiceCatalogItem
                  isSecondary
                  onSuccess={async (item) => {
                    await fetchInvoiceTableDataRef.current?.();
                    const selectedSectionItem = item.category?.name || 'all';
                    const newRecords =
                      cloneDeep(records[selectedSectionItem]) || [];
                    newRecords.push(item);
                    setSelectedSection(selectedSectionItem);
                    setSelectedRecords(newRecords, selectedSectionItem);
                    return true;
                  }}
                />
              )}

              <Button
                primary
                icon={<SVGIcon name="LikeThumb" />}
                onClick={() => {
                  handleSelection();
                }}
                loading={constructedItemsLoading}
              >
                <FormattedMessage id="app.action.done" />
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className="flex center ch-85">
          <div className="w300 fhp p1025 border-top-gray border-right-gray scroll">
            {!props.noAudit && (
              <>
                <NavHead className="fx">
                  <NavHeader>
                    <FormattedMessage id="invoiceBuilder.itemSelection.modal.auditedItems" />
                  </NavHeader>
                </NavHead>

                <div className="LinkGroup mb40">
                  <SelectionItem
                    className="fx actr ticketing-actions"
                    strict
                    selected={selectedSection === 'audit'}
                    key="billing-queue"
                    replace
                    onClick={() => {
                      setSelectedSection('audit');
                      setFilterText(null);
                    }}
                  >
                    <FormattedMessage id="invoiceBuilder.itemSelection.modal.itemsFromBillingQueue" />
                  </SelectionItem>
                </div>
              </>
            )}
            <NavHead className="fx">
              <NavHeader>
                <FormattedMessage id="invoiceBuilder.itemSelection.modal.serviceCategories" />
              </NavHeader>
            </NavHead>
            <div className="LinkGroup mb40">
              <SelectionItem
                className="fx actr ticketing-actions"
                strict
                key="category-all"
                replace
                selected={selectedSection === 'all' || !selectedSection}
                onClick={() => {
                  setSelectedSection('all');
                  setFilterText(null);
                }}
              >
                <FormattedMessage id="invoiceBuilder.itemSelection.allCategories" />
              </SelectionItem>
              {allCategories.map((el) => {
                return (
                  <SelectionItem
                    className="fx actr ticketing-actions"
                    strict
                    key={`category-${el.name}`}
                    replace
                    selected={el.name === selectedSection}
                    onClick={() => {
                      setSelectedSection(el.name);
                      setFilterText(null);
                    }}
                  >
                    {el.label || el.name}
                  </SelectionItem>
                );
              })}
            </div>
          </div>
          <div className="fhp cw-305">
            <ItemsToInvoiceTable
              selectedSection={selectedSection}
              client={props.client}
              site={props.site}
              generateSiteSpecific={generateSiteSpecific}
              setRecords={setSelectedRecords}
              loading={constructedItemsLoading}
              checkDisabledRow={checkDisabledRow}
              checkRowHighlight={checkRowHighlight}
              renderServiceItemSheet={(fetchTableViewData) => {
                if (isNewContracts) {
                  fetchInvoiceTableDataRef.current = fetchTableViewData;
                  return null;
                }
                return (
                  <CustomModal open={showNewServiceItemSheet}>
                    <div className="vh90">
                      <NewServiceCatalog
                        onBack={() => setShowNewServiceItemSheet(false)}
                        onServiceItemCreation={async (serviceItem) => {
                          await fetchTableViewData();
                          const selectedSectionItem =
                            serviceItem.category?.name || 'all';
                          const newRecords =
                            cloneDeep(records[selectedSectionItem]) || [];
                          newRecords.push(serviceItem);
                          setSelectedSection(selectedSectionItem);
                          setSelectedRecords(newRecords, selectedSectionItem);
                          setShowNewServiceItemSheet(false);
                        }}
                        removePadding
                        fullWidth={false}
                      />
                    </div>
                  </CustomModal>
                );
              }}
            />
          </div>
        </div>
      </InvoiceBuilderContextProvider>
    </div>
  );
}

export default injectIntl(ItemsToInvoice);
